/**
 * Contract of the super admin jobs API (`/api/admin/system/jobs`, #2890).
 *
 * This file is the ONLY contract of that API: the backend routes validate
 * with the schemas below and the admin screen types its calls with the
 * interfaces. The generic `/api/jobs` API keeps its own contract in
 * `jobs.types.ts`; the two are not mixed.
 *
 * Dates travel as full ISO-8601 strings. Query parameters arrive as strings
 * (query string), hence the coercions.
 */

import { z } from 'zod/v4'

// ============================================================
// ENUMS
// ============================================================

/** The families the jobs screen groups by; the backend resolves each job to exactly one. */
export const ADMIN_JOB_FAMILIES = [
  'import',
  'market-data',
  'tax-report',
  'export',
  'gdpr',
  'notification',
  'other',
] as const

/** Zod schema of a job family. */
export const AdminJobFamilySchema = z.enum(ADMIN_JOB_FAMILIES)

/** A job family. */
export type AdminJobFamily = z.infer<typeof AdminJobFamilySchema>

/** The six job statuses of `job.schema.ts` (`paused` included so `byStatus` adds up to `total`). */
export const ADMIN_JOB_STATUSES = [
  'pending',
  'processing',
  'paused',
  'completed',
  'failed',
  'cancelled',
] as const

/** Zod schema of a job status. */
export const AdminJobStatusSchema = z.enum(ADMIN_JOB_STATUSES)

/** A job status. */
export type AdminJobStatus = z.infer<typeof AdminJobStatusSchema>

/**
 * What a row of the list or the detail reports when its stored status is not one of
 * the six (`platform_jobs.status` has no CHECK). The row is shown instead of failing
 * the whole read; it is not cancellable and not retryable. `unknown` is NEVER a
 * filter value and never a key of `byStatus`: the summary counts such rows in `total`
 * only.
 */
export const ADMIN_JOB_UNKNOWN_STATUS = 'unknown'

/** Zod schema of the status a row reports: the six, or `unknown`. */
export const AdminJobReportedStatusSchema = z.enum([
  ...ADMIN_JOB_STATUSES,
  ADMIN_JOB_UNKNOWN_STATUS,
])

/** The status a row reports. */
export type AdminJobReportedStatus = z.infer<typeof AdminJobReportedStatusSchema>

/**
 * Why a job cannot be retried: it is not failed, the processor declared the
 * failure permanent, it exhausted its retries, it was already retried (a job
 * replaces it), its family is not retried from this API (`gdpr`) or its stored
 * status is not one the API knows (`unknown_status`, see `ADMIN_JOB_UNKNOWN_STATUS`).
 */
export const ADMIN_JOB_RETRY_BLOCKS = [
  'not_failed',
  'permanent',
  'exhausted',
  'superseded',
  'family',
  'unknown_status',
] as const

/** A retry block reason. */
export type AdminJobRetryBlock = (typeof ADMIN_JOB_RETRY_BLOCKS)[number]

/** Per-element reasons of a bulk or single action: the retry blocks plus the lookup and lock outcomes. */
export const ADMIN_JOB_SKIP_REASONS = [
  ...ADMIN_JOB_RETRY_BLOCKS,
  'not_found',
  'source_busy',
] as const

/** A reason an element was skipped or refused. */
export type AdminJobSkipReason = (typeof ADMIN_JOB_SKIP_REASONS)[number]

// ============================================================
// REQUEST SCHEMAS
// ============================================================

/**
 * A job type or queue name as the platform writes it: `api-import`, `wallet_import`,
 * and also namespaced ones such as `gdpr:data-export` and `audit-report:generate`.
 * The value is always bound as a parameter, never spliced into SQL.
 */
const JOB_TOKEN_PATTERN = /^[A-Za-z0-9_:.-]{1,50}$/

/** The filters the list and the summary share (the repository applies them the same way). */
const JOB_FILTER_SHAPE = {
  family: AdminJobFamilySchema.optional(),
  jobType: z.string().trim().regex(JOB_TOKEN_PATTERN).optional(),
  tenantId: z.string().uuid().optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
}

/** `page` starts at 1; `limit` is capped at 100 rows. */
const PAGINATION_SHAPE = {
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
}

/**
 * `status` accepts a repeated parameter (`?status=failed&status=cancelled`) or
 * a comma-separated one (`?status=failed,cancelled`); an unknown value is a
 * validation error.
 */
const StatusFilterSchema = z
  .union([z.string(), z.array(z.string())])
  .transform((value): string[] | undefined => {
    const parts = (Array.isArray(value) ? value : [value])
      .flatMap((entry) => entry.split(','))
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)
    return parts.length > 0 ? parts : undefined
  })
  .pipe(z.array(AdminJobStatusSchema).optional())
  .optional()

/** Query of `GET /api/admin/system/jobs`. */
export const AdminJobsListQuerySchema = z
  .object({
    ...JOB_FILTER_SHAPE,
    status: StatusFilterSchema,
    q: z.string().trim().min(1).max(100).optional(),
    queue: z.string().trim().regex(JOB_TOKEN_PATTERN).optional(),
    retryable: z
      .enum(['true', 'false'])
      .transform((value) => value === 'true')
      .optional(),
    ...PAGINATION_SHAPE,
    sortBy: z.enum(['createdAt', 'status', 'durationMs']).default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
  })
  .strict()

/** Parsed query of the jobs list. */
export type AdminJobsListQuery = z.infer<typeof AdminJobsListQuerySchema>

/** Query of `GET /api/admin/system/jobs/summary`. */
export const AdminJobsSummaryQuerySchema = z.object(JOB_FILTER_SHAPE).strict()

/** Parsed query of the jobs summary. */
export type AdminJobsSummaryQuery = z.infer<typeof AdminJobsSummaryQuerySchema>

/** Path parameters of the single-job routes. */
export const AdminJobIdParamsSchema = z.object({ id: z.string().uuid() })

/** Body of `POST /api/admin/system/jobs/bulk-retry`. */
export const AdminJobsBulkRetryBodySchema = z
  .object({ ids: z.array(z.string().uuid()).min(1).max(50) })
  .strict()

/** Parsed body of the bulk retry. */
export type AdminJobsBulkRetryBody = z.infer<typeof AdminJobsBulkRetryBodySchema>

// ============================================================
// RESPONSES
// ============================================================

/** The failure of a job, sanitised for the screen. */
export const AdminJobErrorSchema = z.strictObject({
  code: z.string().nullable(),
  message: z.string(),
  permanent: z.boolean(),
})

/** The failure of a job, sanitised for the screen. */
export type AdminJobError = z.infer<typeof AdminJobErrorSchema>

/** What every row of the list carries, whatever its status. */
const JOB_ROW_SHAPE = {
  id: z.string(),
  family: AdminJobFamilySchema,
  jobType: z.string(),
  queueName: z.string(),
  progress: z.number(),
  currentStage: z.string().nullable(),
  tenant: z.strictObject({ id: z.string(), name: z.string() }).nullable(),
  user: z.strictObject({ id: z.string(), email: z.string() }).nullable(),
  subject: z.string().nullable(),
  isRetry: z.boolean(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  updatedAt: z.string(),
  durationMs: z.number().nullable(),
  attempt: z.number(),
  maxRetries: z.number(),
  error: AdminJobErrorSchema.nullable(),
}

/** What every failed row has in common: failed jobs are never cancellable (the branches only differ in their retry state). */
const FAILED_ROW_SHAPE = {
  ...JOB_ROW_SHAPE,
  status: z.literal('failed'),
  cancellable: z.literal(false),
}

/**
 * One row of the jobs list. A union of the LEGAL combinations of status, `cancellable`,
 * `retryable`, `retryBlock` and `replacedByJobId`, so an impossible row (a completed job
 * that is cancellable, an unknown one that is retryable, a replaced one that names no
 * replacement) cannot be typed. Narrow by `status`, then by `retryBlock`.
 *
 * It is a plain `z.union` and not a `z.discriminatedUnion`: the `failed` status has
 * several branches and zod rejects two options with the same discriminator value. The
 * TypeScript type narrows on `status` all the same.
 */
export const AdminJobSchema = z.union([
  // Active: it can be cancelled, it is not failed.
  z.strictObject({
    ...JOB_ROW_SHAPE,
    status: z.enum(['pending', 'processing', 'paused']),
    cancellable: z.literal(true),
    retryable: z.literal(false),
    retryBlock: z.literal('not_failed'),
    replacedByJobId: z.null(),
  }),
  // Finished without failing.
  z.strictObject({
    ...JOB_ROW_SHAPE,
    status: z.enum(['completed', 'cancelled']),
    cancellable: z.literal(false),
    retryable: z.literal(false),
    retryBlock: z.literal('not_failed'),
    replacedByJobId: z.null(),
  }),
  // Failed and retryable.
  z.strictObject({
    ...FAILED_ROW_SHAPE,
    retryable: z.literal(true),
    retryBlock: z.null(),
    replacedByJobId: z.null(),
  }),
  // Failed and blocked for a reason of the job itself.
  z.strictObject({
    ...FAILED_ROW_SHAPE,
    retryable: z.literal(false),
    retryBlock: z.enum(['permanent', 'exhausted', 'family']),
    replacedByJobId: z.null(),
  }),
  // Failed and already retried: the replacement is always named.
  z.strictObject({
    ...FAILED_ROW_SHAPE,
    retryable: z.literal(false),
    retryBlock: z.literal('superseded'),
    replacedByJobId: z.string(),
  }),
  // A stored status the API does not know: neither cancellable nor retryable.
  z.strictObject({
    ...JOB_ROW_SHAPE,
    status: z.literal(ADMIN_JOB_UNKNOWN_STATUS),
    cancellable: z.literal(false),
    retryable: z.literal(false),
    retryBlock: z.literal('unknown_status'),
    replacedByJobId: z.null(),
  }),
])

/** One row of the jobs list. */
export type AdminJob = z.infer<typeof AdminJobSchema>

/** Response of the jobs list. */
export interface AdminJobsListResponse {
  items: AdminJob[]
  total: number
  page: number
  limit: number
}

/** Number of jobs per status. */
export type AdminJobStatusCounts = Record<AdminJobStatus, number>

/** Counts of one family inside the summary. */
export interface AdminJobFamilyCounts {
  total: number
  failed: number
  retryableFailed: number
}

/** Response of the jobs summary. */
export interface AdminJobsSummary {
  /** Every job counted: `total = Σ byStatus + unknownStatus`. */
  total: number
  byStatus: AdminJobStatusCounts
  /** Jobs whose stored status is not one of the six: in `total`, in no key of `byStatus`. */
  unknownStatus: number
  byFamily: Record<AdminJobFamily, AdminJobFamilyCounts>
  retryableFailed: number
}

/** One entry of a job's event timeline. */
export interface AdminJobEvent {
  id: string
  type: string
  at: string
  message: string | null
  progress: number | null
}

/** The data source a job ran for, and whether it still exists. */
export interface AdminJobSource {
  id: string
  name: string | null
  exists: boolean
}

/** Response of the job detail. */
export type AdminJobDetail = AdminJob & {
  inputData: Record<string, unknown>
  outputData: Record<string, unknown> | null
  source: AdminJobSource | null
  retriedFromJobId: string | null
  events: AdminJobEvent[]
}

/** Response of a single retry: the job that was queued, always `pending` (a new job, or the same one requeued). */
export interface AdminJobRetryResult {
  jobId: string
  status: 'pending'
}

/** Response of a cancel: the job ends `cancelled`. */
export interface AdminJobCancelResult {
  status: 'cancelled'
}

/**
 * `details` of `JOB_BULK_RETRY_FAILED`, the error of an aborted bulk retry (the whole batch
 * is rolled back): the job that failed, the position of its FIRST appearance in the
 * request body (`ids`, as sent, before lower-casing and removing duplicates) and why.
 * `cause.code` is the code of the original error when it was an API error, else the
 * PostgreSQL SQLSTATE when there is one, else `INTERNAL`; `cause.message` is sanitised
 * (no SQL, no paths, UUIDs as `[id]`, e-mail addresses as `[email]`). The HTTP status of the
 * error is the original's, EXCEPT 401, 403 and 404, which a client would take for its own
 * session or screen: those are answered as 500.
 */
export const AdminJobBulkFailureDetailsSchema = z.strictObject({
  jobId: z.string(),
  index: z.number().int().min(0),
  cause: z.strictObject({ code: z.string(), message: z.string() }),
})

/** `details` of `JOB_BULK_RETRY_FAILED`. */
export type AdminJobBulkFailureDetails = z.infer<typeof AdminJobBulkFailureDetailsSchema>

/**
 * Response of the bulk retry: what was retried and what was skipped, with the reason.
 * Only a missing job or a refusal is skipped. Any other failure aborts the request
 * and rolls the WHOLE batch back (all or nothing on a server error), a deadlock
 * (40P01) between two concurrent batches included; the lists keep the order of the
 * request, with the ids in lower case. The error of an aborted batch is ALWAYS
 * `JOB_BULK_RETRY_FAILED`, with `AdminJobBulkFailureDetails` (the original code in `cause`).
 */
export interface AdminJobsBulkRetryResult {
  retried: Array<{ id: string; newJobId: string }>
  skipped: Array<{ id: string; reason: AdminJobSkipReason }>
}

/**
 * The `details` of `JOB_NOT_RETRYABLE` (the 409 of a retry): the current status and why.
 * Only `superseded` says who replaced the job and when. Each reason is tied to the
 * statuses it can happen with: a job that is not `failed` is `not_failed`; the other
 * reasons are about a `failed` job. A busy source (`JOB_SOURCE_BUSY`), a family that is not
 * retried from the panel (`JOB_FAMILY_NOT_RETRYABLE`) and a job of unknown status
 * (`JOB_STATUS_UNKNOWN`, with the raw status) are codes of their own; `JOB_NOT_FOUND` has
 * no `details`.
 */
export const AdminJobRetryConflictDetailsSchema = z.union([
  z.strictObject({
    status: z.literal('failed'),
    reason: z.literal('superseded'),
    replacedByJobId: z.string(),
    retriedBy: z.strictObject({ id: z.string(), email: z.string() }).nullable(),
    retriedAt: z.string().nullable(),
  }),
  z.strictObject({
    status: z.literal('failed'),
    reason: z.enum(['permanent', 'exhausted']),
  }),
  z.strictObject({
    status: AdminJobStatusSchema.exclude(['failed']),
    reason: z.literal('not_failed'),
  }),
])

/** `details` of `JOB_NOT_RETRYABLE`. */
export type AdminJobRetryConflictDetails = z.infer<typeof AdminJobRetryConflictDetailsSchema>

/** The reasons of `JOB_NOT_RETRYABLE` that are not a replacement: they carry no replacement data. */
export type AdminJobRetryRefusalReason = Exclude<
  AdminJobRetryConflictDetails['reason'],
  'superseded'
>

/** `details` of `JOB_NOT_CANCELLABLE` (the 409 of a cancel): only the current status, never a reason. */
export const AdminJobCancelConflictDetailsSchema = z.strictObject({
  status: AdminJobStatusSchema,
})

/** `details` of `JOB_NOT_CANCELLABLE`. */
export type AdminJobCancelConflictDetails = z.infer<typeof AdminJobCancelConflictDetailsSchema>

/**
 * `details` of `JOB_SOURCE_BUSY` and `JOB_FAMILY_NOT_RETRYABLE` (409 of a retry): the job is
 * `failed`, it is the source or the family that stops it.
 */
export const AdminJobRetryUnavailableDetailsSchema = z.strictObject({
  status: z.literal('failed'),
})

/** `details` of `JOB_SOURCE_BUSY` and `JOB_FAMILY_NOT_RETRYABLE`. */
export type AdminJobRetryUnavailableDetails = z.infer<typeof AdminJobRetryUnavailableDetailsSchema>

/**
 * `details` of `JOB_STATUS_UNKNOWN` (409 of a retry AND of a cancel of a job whose stored
 * status is not one of the six): `status` is the RAW value read from the row, not the word
 * `unknown`, so the operator can tell what the row holds.
 */
export const AdminJobStatusUnknownDetailsSchema = z.strictObject({ status: z.string() })

/** `details` of `JOB_STATUS_UNKNOWN`. */
export type AdminJobStatusUnknownDetails = z.infer<typeof AdminJobStatusUnknownDetailsSchema>

/** `details` of `INVALID_JOB_TYPE` (400): the family and the job type that do not go together. */
export const AdminJobInvalidTypeDetailsSchema = z.strictObject({
  family: AdminJobFamilySchema,
  jobType: z.string(),
})

/** `details` of `INVALID_JOB_TYPE`. */
export type AdminJobInvalidTypeDetails = z.infer<typeof AdminJobInvalidTypeDetailsSchema>

/**
 * Every error code of this API and the shape of its `details` (`undefined`: none). The
 * backend builds each error through this map, so the compiler rejects a `details` that
 * belongs to another code, and the screen can narrow `error.details` by `error.code`.
 */
export type AdminJobErrorDetailsByCode = {
  JOB_NOT_FOUND: undefined
  INVALID_JOB_TYPE: AdminJobInvalidTypeDetails
  JOB_NOT_RETRYABLE: AdminJobRetryConflictDetails
  JOB_NOT_CANCELLABLE: AdminJobCancelConflictDetails
  JOB_SOURCE_BUSY: AdminJobRetryUnavailableDetails
  JOB_FAMILY_NOT_RETRYABLE: AdminJobRetryUnavailableDetails
  JOB_STATUS_UNKNOWN: AdminJobStatusUnknownDetails
  JOB_BULK_RETRY_FAILED: AdminJobBulkFailureDetails
}
